import AddLocationAltIcon from "@mui/icons-material/AddLocationAlt";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CloseIcon from "@mui/icons-material/Close";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import { Box, Button, Checkbox, Chip, Typography, useTheme } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import cuid from "cuid";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";

import FleetMessageSuccess from "@/components/FleetMessageSuccess.tsx";
import GeoTargetRecipients from "@/components/GeoTargetRecipients";
import PaperAirplane from "@/components/Icons/PaperAirplane";
import ListTemplates from "@/components/ListTemplates";
import Loading from "@/components/Loading/Index.tsx";
import MyGroups from "@/components/MyGroups.tsx";
import SelectRecipients from "@/components/SelectRecipients";
import TextMessageInput from "@/components/TextMessageInput";
import TusHooks from "@/hooks/UseTusHooks/TusHooks.tsx";
import Locator from "@/locator";
import { ActionContext } from "@/models/ActionsProvider";
import { bootstrapWorkspaceFeedPermissions, loadBootstrapEventAndHydrateLocalDB } from "@/models/BootstrapUtils.ts";
import { DuplicateAliasAccount } from "@/models/repositories";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { TrackingContext } from "@/models/TrackingStateProvider";
import { UxContext } from "@/models/UxStateProvider";
import { pluralValue } from "@/utils";
import { checkForDuplicateAccounts } from "@/utils/feed.ts";
import ButtonTabs from "./ButtonTabs";

export default function FleetMessaging({
  closeModal,
  fleetMessageText,
  setFleetMessageText,
}: {
  closeModal: () => void;
  fleetMessageText: string;
  setFleetMessageText: (value: string) => void;
}) {
  const {
    fleetMessageInvalidLocation,
    fleetMessageActiveChannels,
    fleetMessageActiveSection,
    setFleetMessageLatitude,
    setFleetMessageLongitude,
    setFleetMessageLocation,
    setFleetMessageActiveChannels,
    fleetMessageFromDriverTable,
    setFleetMessageFromDriverTable,
  } = useContext(UxContext);
  const { currentWorkspaceId: workspaceId } = useContext(WorkspaceContext);
  const { ampli } = useContext(TrackingContext);
  const { publishBroadcast } = useContext(ActionContext);
  const theme = useTheme();
  const [disabledButton, setDisabledButton] = useState<boolean>(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
  const [showTemplates, setShowTemplates] = useState<boolean>(false);
  const [showUpload, setShowUpload] = useState<boolean>(false);
  const [serverError, setServerError] = useState<string[]>([]);
  const [temporaryChannels, setTemporaryChannels] = useState<string[]>([]);
  const [unqueKey, setUniqueKey] = useState<string>("initial");
  const [activeTab, setActiveTab] = useState<string>(fleetMessageActiveSection ?? "select-recipients");
  const [saveGroupForFutureUse, setSaveGroupForFutureUse] = useState<boolean>(false);

  useEffect(() => {
    if (fleetMessageActiveSection) {
      setActiveTab(fleetMessageActiveSection);
    }
  }, [fleetMessageActiveSection]);
  const [hasDuplicatesMessage, setHasDuplicatesMessage] = useState<boolean>(false);
  const [hasDuplicates, setHasDuplicates] = useState<DuplicateAliasAccount[]>([]);
  const [error, setError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [uploadContentId, setUploadContentId] = useState<string | null>(null);

  const handleUploadContent = useCallback(
    (value: { itemId: string; contentId: string }) => {
      setUploadContentId(value.contentId);
    },
    [setUploadContentId],
  );

  const toggleShowTemplates = () => {
    setShowTemplates((value) => !value);
    setShowUpload(() => false);
    if (!showTemplates) {
      ampli.openTemplatesFleetMessage();
    }
  };

  const resetAllFleetValues = () => {
    setFleetMessageActiveChannels(() => []);
    setFleetMessageLatitude(() => "");
    setFleetMessageLongitude(() => "");
    setFleetMessageText("");
    setFleetMessageLocation(() => "");
    setFleetMessageFromDriverTable(() => false);
  };

  useEffect(() => {
    if (!workspaceId) {
      return;
    }

    let isCancelled = false;

    checkForDuplicateAccounts({
      feedIds: fleetMessageActiveChannels,
      workspaceId,
    }).then((data) => {
      if (!isCancelled) {
        setHasDuplicatesMessage(() => data.length > 0);
        setHasDuplicates(() => data);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [fleetMessageActiveChannels, workspaceId]);

  const toggleAllGroupFeeds = useCallback(
    (ids: string[]) => {
      setFleetMessageActiveChannels((prev) => {
        const hasSome = ids.some((feedId) => prev.includes(feedId));
        if (hasSome) {
          return prev.filter((feedId) => !ids.includes(feedId));
        } else {
          return [...new Set([...prev, ...ids])];
        }
      });
    },
    [setFleetMessageActiveChannels],
  );

  useEffect(() => {
    if (!workspaceId) {
      return;
    }

    let isCancelled = false;

    setLoading(true);
    loadBootstrapEventAndHydrateLocalDB({
      workspaceId,
      bootstrapEvent: bootstrapWorkspaceFeedPermissions,
      pageSize: 10000,
      page: 0,
    })
      .then(() => {
        if (!isCancelled) {
          setError(false);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setError(true);
          setLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [workspaceId]);

  const setActiveIds = useCallback(
    (values: string[]) => setFleetMessageActiveChannels(values),
    [setFleetMessageActiveChannels],
  );

  const mutateKey = useCallback((value: string) => setUniqueKey(() => value), []);

  const tabs = useMemo(
    () =>
      [
        {
          key: "select-recipients",
          icon: <CheckBoxIcon />,
          component: (
            <Box>
              <SelectRecipients
                driverTableList={fleetMessageFromDriverTable}
                allowNonAliasChannels={false}
                activeChannels={fleetMessageActiveChannels}
                toggleAllIds={toggleAllGroupFeeds}
                setActiveIds={setActiveIds}
              />
              {fleetMessageActiveChannels?.length > 0 && (
                <Box>
                  <label>
                    <Checkbox
                      checked={saveGroupForFutureUse}
                      onChange={(value) => {
                        setSaveGroupForFutureUse((value: boolean) => !value);
                        ampli.toggleSaveFleetMessageGroup({
                          checked: value?.target?.checked,
                        });
                      }}
                    />
                    Save group for future use
                  </label>
                </Box>
              )}
            </Box>
          ),
          text: Locator.feed.input.fleetMessage.modal.selectRecipients,
        },
        {
          key: "my-groups",
          icon: <PeopleAltIcon />,
          component: (
            <MyGroups
              setActiveIds={setActiveIds}
              activeChannels={fleetMessageActiveChannels}
              key={unqueKey}
              mutateKey={mutateKey}
            />
          ),
          text: (
            <Box>
              {Locator.feed.input.fleetMessage.modal.myGroups}
              <Box
                component="span"
                sx={{
                  position: "absolute",
                  fontSize: "10px",
                  px: 0.25,
                  borderRadius: "2px",
                  right: 0,
                  top: 0,
                  background: theme.palette.info.main,
                }}
              >
                beta
              </Box>
            </Box>
          ),
        },
        {
          key: "geo-target",
          icon: <AddLocationAltIcon />,
          component: (
            <Box>
              <GeoTargetRecipients fleetMessageInvalidLocation={fleetMessageInvalidLocation} />
            </Box>
          ),
          text: Locator.feed.input.fleetMessage.modal.geoLocation,
        },
      ].map((tab) => ({ ...tab, active: tab.key === activeTab })),
    [
      fleetMessageActiveChannels,
      fleetMessageFromDriverTable,
      fleetMessageInvalidLocation,
      saveGroupForFutureUse,
      activeTab,
      unqueKey,
      toggleAllGroupFeeds,
      setActiveIds,
      mutateKey,
      ampli,
      theme,
    ],
  );
  const activeComponent = tabs.find((state) => state.active)?.component;
  const activeTabKey = tabs?.find((tab) => tab.active)?.key;

  const resetAfterSubmit = () => {
    ampli.sendFleetMessage({ source: activeTabKey });
    setUploadContentId(null);
    setShowSuccessMessage(() => true);
    setTemporaryChannels(() => fleetMessageActiveChannels);
    setFleetMessageActiveChannels(() => []);
    setFleetMessageText("");
    setFleetMessageActiveChannels([]);
    setDisabledButton(() => false);
    setFleetMessageText("");
    setShowUpload(() => false);
    setServerError(() => []);
  };

  const errorOverride = (code: number, message: string): string[] => {
    if (code === 403) {
      return [
        "Fleet message failed to send.",
        "Please refresh page and try again.",
        "Contact support if you continue to experience issues  +1 (844) 619-0500.",
      ];
    }
    if (!message) {
      return ["An error occurred while sending a fleet message."];
    }
    return [message];
  };

  const submitWithAttachment = async () => {
    if (!fleetMessageActiveChannels || fleetMessageActiveChannels?.length === 0) {
      setServerError(() => ["Please add at least one recipient before sending."]);
      return;
    }
    setDisabledButton(() => true);

    try {
      const payload = {
        workspaceId,
        feedIds: fleetMessageActiveChannels,
        contentId: uploadContentId,
      };
      await publishBroadcast(payload);
      resetAfterSubmit();
    } catch (e) {
      setServerError(() => errorOverride(e?.status ?? e?.statusCode, e?.error?.message));
      setDisabledButton(() => false);
      setUploadContentId(null);
      return Promise.reject(e);
    }
  };

  const submitForm = async () => {
    if (!fleetMessageActiveChannels || fleetMessageActiveChannels?.length === 0) {
      setServerError(() => ["Please add at least one recipient before sending."]);
      return;
    }
    setDisabledButton(() => true);
    const payload = {
      workspaceId,
      contentId: cuid(),
      feedIds: fleetMessageActiveChannels,
      inputText: fleetMessageText,
    };

    try {
      await publishBroadcast(payload);
      resetAfterSubmit();
    } catch (e) {
      setServerError(() => errorOverride(e?.status ?? e?.statusCode, e?.error?.message));
      setDisabledButton(() => false);
      return Promise.reject(e);
    }
  };

  const disabledState = () => {
    const missingActiveChannels = fleetMessageActiveChannels?.length === 0;
    const tooManyCharacters = !showUpload && fleetMessageText?.length >= 5000;
    const noFleetText = !showUpload && fleetMessageText?.length <= 0;

    if (disabledButton) {
      return true;
    }

    if (missingActiveChannels) {
      return true;
    }
    if (tooManyCharacters) {
      return true;
    }
    if (noFleetText) {
      return true;
    }
    return showUpload && !uploadContentId;
  };

  const disabledValue = disabledState();

  const messageLabelText = `${
    fleetMessageActiveChannels?.length
  } Recipient${pluralValue(fleetMessageActiveChannels?.length)}`;

  const disabledAddFileButton = showTemplates || fleetMessageText?.length > 0 || disabledButton;

  const tabReset = () => {
    setFleetMessageActiveChannels([]);
    setFleetMessageLocation("");
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: "300px",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <Box>Loading Permissions</Box>
        <Loading variant="white" size="large" baseSize={6} />
      </Box>
    );
  } else if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: "300px",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <Box>
          <Box sx={{ mb: 2 }}>Error Loading Permissions</Box>
          <Button onClick={() => window.location.reload()}>Click To Refresh</Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "300px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {showSuccessMessage ? (
        <FleetMessageSuccess
          saveGroupForFutureUse={saveGroupForFutureUse}
          setSaveGroupForFutureUse={() => setSaveGroupForFutureUse(() => false)}
          temporaryChannels={temporaryChannels}
          setShowSuccessMessage={(value: boolean) => {
            setShowSuccessMessage(() => value);
            resetAllFleetValues();
          }}
        />
      ) : (
        <Box sx={{ width: "100%" }}>
          <Box sx={{ display: "flex", gap: 2, height: "450px" }}>
            <Box sx={{ width: "40%", position: "relative" }}>
              {hasDuplicatesMessage && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 96,
                    borderRadius: "0.5rem",
                    alignItems: "center",
                    width: "calc(100% - 8px)",
                    left: 4,
                    right: 0,
                    background: theme.palette.error.light,
                    p: 2,
                    pr: 4,
                    zIndex: 10,
                    maxHeight: "322px",
                    overflowY: "auto",
                    display: "block",
                  }}
                >
                  <IconButton
                    sx={{ position: "absolute", top: 4, right: 4 }}
                    size="small"
                    aria-label="close"
                    color="primary"
                    onClick={(e) => setHasDuplicatesMessage(() => false)}
                  >
                    <CloseIcon role="img" />
                  </IconButton>
                  <Box sx={{ mb: 1 }}>
                    The following ({hasDuplicates?.length}) drivers will receive the message more than once if you
                    proceed with this selection:{" "}
                  </Box>
                  <Box
                    sx={{
                      fontSize: "12px",
                    }}
                  >
                    {hasDuplicates.map((dupe) => dupe.name).join(", ")}
                  </Box>
                </Box>
              )}
              {serverError?.length > 0 && (
                <Box
                  sx={{
                    borderRadius: "0.5rem",
                    display: "flex",
                    alignItems: "center",
                    position: "absolute",
                    background: theme.palette.error.light,
                    textAlign: "center",
                    py: 6,
                    px: 2,
                  }}
                >
                  <IconButton
                    sx={{ position: "absolute", top: 4, right: 4 }}
                    size="small"
                    aria-label="close"
                    color="primary"
                    onClick={(e) => setServerError(() => [])}
                  >
                    <CloseIcon role="img" />
                  </IconButton>
                  <Box>
                    {serverError?.map((error) => (
                      <Box sx={{ mb: 1 }}>{error}</Box>
                    ))}
                  </Box>
                </Box>
              )}
              {!showUpload ? (
                <TextMessageInput
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                        }}
                      >
                        Message
                      </Typography>

                      {messageLabelText && (
                        <Chip
                          label={messageLabelText}
                          size="small"
                          sx={{
                            background:
                              fleetMessageActiveChannels?.length > 0
                                ? theme.palette.success.light
                                : theme.palette.secondary.light,
                          }}
                        />
                      )}
                    </Box>
                  }
                  rows={13}
                  textInputValue={fleetMessageText}
                  textInputHandler={(e) => setFleetMessageText(e)}
                  showHint={false}
                  required={true}
                  aria-label={Locator.feed.input.fleetMessage.modal.tts}
                />
              ) : (
                <Box sx={{ height: "calc(100% - 69px)", mb: 2 }}>
                  {serverError?.length > 0 ? (
                    <></>
                  ) : (
                    <TusHooks autoClick={true} setUploadContent={handleUploadContent} />
                  )}
                </Box>
              )}

              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  disabled={showUpload}
                  variant={showTemplates ? "contained" : "outlined"}
                  sx={{ display: "flex", gap: 2 }}
                  onClick={toggleShowTemplates}
                >
                  <span>Templates</span>
                  {showTemplates && (
                    <Box sx={{ display: "flex" }}>
                      <CloseIcon sx={{ fontSize: 15 }} />
                    </Box>
                  )}
                </Button>
                <Button
                  disabled={disabledAddFileButton}
                  variant={showUpload ? "contained" : "outlined"}
                  sx={{ display: "flex", alignItems: "center", gap: 2 }}
                  onClick={() => {
                    setShowUpload((value) => !value);
                    setShowTemplates(() => false);
                    if (uploadContentId) {
                      setUploadContentId(() => null);
                    }
                  }}
                >
                  {uploadContentId ? <span>Remove File</span> : <span>Add File</span>}

                  {showUpload && (
                    <Box sx={{ display: "flex" }}>
                      <CloseIcon sx={{ fontSize: 15 }} />
                    </Box>
                  )}
                </Button>
              </Box>
            </Box>
            <Box sx={{ width: "60%", position: "relative" }}>
              {showTemplates && (
                <ListTemplates
                  hideTemplates={() => setShowTemplates(() => false)}
                  setTemplateText={(value) => setFleetMessageText(value)}
                />
              )}
              <ButtonTabs
                tabs={tabs}
                setActiveTab={(value) => {
                  tabReset();
                  setActiveTab(value);
                  setSaveGroupForFutureUse(() => false);
                }}
              />
              <Box sx={{ mt: 1 }}>{activeComponent}</Box>
            </Box>
          </Box>
          <Box sx={{ display: "flex", mt: 1, gap: 2 }}>
            <Box sx={{ width: "50%" }}>
              <Button
                variant="outlined"
                sx={{ order: { xs: 1, sm: 0 } }}
                onClick={() => closeModal()}
                aria-label={Locator.feed.input.fleetMessage.modal.cancel}
                type="button"
              >
                Cancel
              </Button>
            </Box>
            <Box sx={{ width: "50%" }}>
              <Button
                onClick={() => (uploadContentId ? submitWithAttachment() : submitForm())}
                variant="contained"
                color="primary"
                sx={{ display: "flex", gap: 1 }}
                disabled={disabledValue}
                type="button"
                aria-label={Locator.feed.input.fleetMessage.modal.continue}
              >
                <Box>Send</Box>
                <PaperAirplane
                  style={{
                    fill: disabledValue ? theme.palette.neutral.main : theme.palette.primary.main,
                    height: "20px",
                    width: "20px",
                  }}
                />
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
